import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DormantOpenIcon } from '../../../../components/icons/DormantIcon'
import { db } from '../../../../lib/db/indexeddb'
import { scheduleSync } from '../../../../lib/sync/sync-engine'
import { useSoftAddSound } from '../../../../lib/sounds'
import { TaskList, type TaskListHandle } from '../../components/TaskList'
import type { TaskEntryData } from '../../components/TaskEntry'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { cloneTaskFields } from '../../utils/cloneTaskFields'
import { taskToEntryData } from '../../utils/taskToEntryData'
import { useStableLiveQuery } from '../../../../lib/db/useStableLiveQuery'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { useDormantTasks } from './hooks/useDormantTasks'
import '../TaskViews.scss'
import './DormantPage.scss'

interface DormantPageProps {
  lifeSpace: 'personal' | 'work' | 'home'
}

export const DormantPage: React.FC<DormantPageProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  const isActive = useIsActiveTab()
  const rawTasks = useDormantTasks({ lifeSpace, isActive })
  const taskListRef = useRef<TaskListHandle | null>(null)
  const playSoftAdd = useSoftAddSound()

  // Optimistic overrides so toggle/edit/badge-update paint in the next frame
  // instead of waiting on the IDB write + LiveQuery refire.
  const { optimisticTaskOverrides, setOptimisticTaskOverride } = useOptimisticTaskOverrides(rawTasks)

  // Merge overrides and drop rows that optimistically left the Ruhend view
  // (un-paused, completed, or soft-deleted) — otherwise a just-completed task
  // would linger for a second until the LiveQuery catches up.
  const effectiveTasks = useMemo(() => {
    if (!rawTasks) return rawTasks
    if (optimisticTaskOverrides.size === 0) return rawTasks
    return rawTasks.flatMap(task => {
      const override = optimisticTaskOverrides.get(task.id)
      const merged = override ? { ...task, ...override } : task
      if (merged.deletedAt) return []
      if (merged.pausedAt == null) return []
      if (merged.status === 'completed') return []
      return [merged]
    })
  }, [rawTasks, optimisticTaskOverrides])

  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // `cloneTaskFields` preserves `pausedAt` by default, so a duplicate of a
  // dormant task lands right back in Ruhend without an extra update-write.
  const duplicateTasks = useCallback(async (ids: string[]) => {
    if (!rawTasks || ids.length === 0) return
    const byId = new Map(rawTasks.map(task => [task.id, task]))
    const newIds: string[] = []
    for (const id of ids) {
      const source = byId.get(id)
      if (!source) continue
      const created = await db.createTask(cloneTaskFields(source))
      newIds.push(created.id)
    }
    newIds.forEach((_, i) => setTimeout(() => playSoftAdd(), i * 80))
    if (newIds.length > 0) taskListRef.current?.selectTasks(newIds)
    scheduleSync()
  }, [rawTasks, playSoftAdd])

  // Context-menu duplicate: if the clicked task is part of a multi-selection,
  // duplicate the whole selection; otherwise just the clicked task.
  const handleDuplicate = useCallback((clickedTaskId: string) => {
    const selection = taskListRef.current?.getSelection() ?? new Set<string>()
    const ids = selection.size > 1 && selection.has(clickedTaskId)
      ? Array.from(selection)
      : [clickedTaskId]
    void duplicateTasks(ids)
  }, [duplicateTasks])

  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => taskListRef.current?.getSelection() ?? new Set<string>(),
    setOptimisticTaskOverride,
    logTag: 'DormantPage',
  })

  // Look up project names so the inline ProjectBadge in each row can show
  // the parent project. Independent LiveQuery (vs. piggy-backing on
  // `useDormantTasks`) keeps the hook's contract focused on tasks.
  const projects = useStableLiveQuery(
    () => db.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => !p.deletedAt)
      .toArray(),
    [lifeSpace],
  )
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>()
    projects?.forEach(p => map.set(p.id, p.name))
    return map
  }, [projects])

  const taskEntries = useMemo<TaskEntryData[]>(
    () => effectiveTasks
      ? effectiveTasks.map(task => {
          const entry = taskToEntryData(task)
          if (!task.projectId) return entry
          const projectName = projectNameMap.get(task.projectId)
          return projectName ? { ...entry, projectName } : entry
        })
      : [],
    [effectiveTasks, projectNameMap],
  )

  // Re-focus the list whenever this page becomes active. TabStack's
  // `display: none` on inactive slots drops focus to <body>, so without
  // this the user would have to click the list once after every tab
  // switch to get ArrowKey/Delete back. rAF defers past the tab-swap
  // paint, and we skip the refocus when the user is mid-typing somewhere
  // else (e.g. inline task edit) so focus doesn't yank out from under them.
  useEffect(() => {
    if (!isActive) return
    const rafId = requestAnimationFrame(() => {
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      taskListRef.current?.focusList()
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive])

  if (rawTasks === undefined) {
    return <div className="task-view dormant-page" aria-busy="true" />
  }

  return (
    <div className="task-view dormant-page">
      <div className="dormant-page__header">
        <h1 className="dormant-page__title">
          <DormantOpenIcon size={18} className="dormant-page__title-icon" />
          {t('dormant.title')}
        </h1>
        <p className="dormant-page__subtitle">{t('dormant.subtitle')}</p>
      </div>

      <div className="dormant-page__scroll">
        {taskEntries.length === 0 ? (
          <div className="dormant-page__empty">{t('dormant.empty')}</div>
        ) : (
          <TaskList
            tasks={taskEntries}
            handleRef={taskListRef}
            stateKey={`dormant-${lifeSpace}`}
            onToggleComplete={handleToggleComplete}
            onDelete={handleDeleteTask}
            onDuplicate={handleDuplicate}
            onContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
            onUpdateTask={handleUpdateTask}
          />
        )}
      </div>
    </div>
  )
}
